import { getSchemaText } from '@principle-theorem/editor';
import {
  CalendarUnit,
  CalendarView,
  CollectionGroup,
  EventType,
  IAppointment,
  IBrand,
  ICalendarEvent,
  ICalendarEventSchedule,
  ICalendarEventSchedulesMap,
  IEventable,
  IPatient,
  IPractice,
  IScheduleSummary,
  IScheduleSummaryEvent,
  IScheduleSummaryEventable,
  IStaffer,
  getRangeForDisplay,
  isCalendarEvent,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  IReffable,
  ISODateType,
  ITimePeriod,
  ITimestampRange,
  RequireProps,
  Timezone,
  WithRef,
  all$,
  asyncForEach,
  collectionGroupQuery,
  durationToHumanisedTime,
  isSameIdentifier,
  isSameRef,
  multiFilter,
  multiMap,
  multiSwitchMap,
  overlappingChunkRange,
  query$,
  reduce2DArray,
  reduceToSingleArray,
  safeCombineLatest,
  toEntityModel,
  toEntityModels$,
  toISODate,
  toMomentTz,
  toTimePeriod,
  where,
} from '@principle-theorem/shared';
import { chunk, compact, groupBy, uniqWith } from 'lodash';
import { duration } from 'moment-timezone';
import { Observable, combineLatest, from, iif, of } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { Appointment } from './appointment/appointment';
import { Brand } from './brand';
import { stafferToParticipant } from './common';
import { CalendarEventSchedule } from './event/calendar-event-schedule';
import { Event, filterEventTypes } from './event/event';
import { Gap } from './gap/gap';
import { OrganisationCache } from './organisation/organisation-cache';
import { Practice } from './practice/practice';
import { ScheduleSummary } from './schedule-summary/schedule-summary';
import { Roster } from './staffer/roster';
import { Staffer } from './staffer/staffer';

export class EventableQueries {
  static allAppointments$(
    practice: IReffable<IPractice>,
    range: ITimePeriod,
    participants?: DocumentReference<IStaffer | IPatient>[]
  ): Observable<WithRef<RequireProps<IAppointment, 'event'>>[]> {
    return all$(
      collectionGroupQuery<RequireProps<IAppointment, 'event'>>(
        CollectionGroup.Appointments,
        ...compact([
          where('event.practice.ref', '==', practice.ref),
          where('event.from', '>=', range.from.toDate()),
          where('event.from', '<=', range.to.toDate()),
          participants && participants.length
            ? where('event.participantRefs', 'array-contains-any', participants)
            : undefined,
        ])
      )
    ).pipe(multiFilter((appointment) => !appointment.deleted));
  }

  static appointments$(
    brand: IReffable<IBrand>,
    range: ITimePeriod,
    participants?: DocumentReference<IStaffer | IPatient>[],
    practices?: IReffable<IPractice>[]
  ): Observable<WithRef<RequireProps<IAppointment, 'event'>>[]> {
    return iif(
      () => !!practices?.length,
      of(practices ?? []),
      Brand.practices$(brand).pipe(
        multiMap((practice) => ({ ref: practice.ref }))
      )
    ).pipe(
      multiSwitchMap((practice) => {
        if (!participants || participants.length < 10) {
          return EventableQueries.allAppointments$(
            practice,
            range,
            participants
          );
        }

        const participantGroups = chunk(participants, 10);
        return safeCombineLatest(
          participantGroups.map((group) =>
            EventableQueries.allAppointments$(practice, range, group)
          )
        ).pipe(map(reduceToSingleArray));
      }),
      map(reduceToSingleArray)
    );
  }

  static getEventables$<T extends object>(
    brand: IReffable<IBrand>,
    range: ITimePeriod,
    excludeTypes: EventType[] = [],
    participants?: DocumentReference<IStaffer | IPatient>[],
    practiceRef?: DocumentReference<IPractice>
  ): Observable<WithRef<IEventable<T>>[]> {
    const appointments$ = excludeTypes.includes(EventType.Appointment)
      ? of([])
      : (EventableQueries.appointments$(
          brand,
          range,
          participants,
          practiceRef ? [{ ref: practiceRef }] : undefined
        ) as Observable<WithRef<IEventable<IAppointment>>[]>);

    const events$ = Brand.getCalendarEvents$(
      brand,
      range,
      participants,
      undefined,
      practiceRef
    );

    return combineLatest([appointments$, events$]).pipe(
      map(
        ([appointments, events]) =>
          [...appointments, ...events] as WithRef<IEventable<T>>[]
      ),
      filterEventTypes<T>(excludeTypes)
    );
  }

  static getEventablesLegacy$(
    brand: WithRef<IBrand>,
    practice: WithRef<IPractice>,
    view: CalendarView,
    range: ITimePeriod,
    unit: CalendarUnit,
    timezone: Timezone,
    brandPractitioners: WithRef<IStaffer>[],
    excludeTypes: EventType[] = []
  ): Observable<IScheduleSummaryEventable<IAppointment | ICalendarEvent>[]> {
    const displayRange = getRangeForDisplay(view, unit, range, timezone);

    const appointments$ = EventableQueries.allAppointments$(
      practice,
      displayRange,
      brandPractitioners.map((practitioner) => practitioner.ref)
    ).pipe(
      multiSwitchMap((appointment) =>
        query$(
          Appointment.interactionCol(appointment),
          where('pinned', '==', true)
        ).pipe(
          multiFilter((interaction) => !interaction.deleted),
          map(() => appointment)
        )
      )
    ) as Observable<WithRef<IEventable<IAppointment>>[]>;

    const events$ = Brand.getCalendarEvents$(
      brand,
      displayRange,
      brandPractitioners.map((practitioner) => practitioner.ref),
      undefined,
      practice.ref
    ).pipe(
      filterEventTypes<ICalendarEvent>([
        EventType.RosteredOn,
        EventType.GapCandidate,
      ])
    );

    const blockingSchedules$ = safeCombineLatest(
      brandPractitioners.map((brandPractitioner) =>
        Roster.all$(
          brandPractitioner,
          [EventType.PreBlock, EventType.Break],
          practice
        )
      )
    ).pipe(map((schedules) => schedules.flat()));

    return combineLatest([appointments$, events$, blockingSchedules$]).pipe(
      switchMap(([appointments, events, blockingSchedules]) =>
        legacyScheduleSummaryEventables$(
          displayRange,
          blockingSchedules,
          appointments,
          events,
          brandPractitioners
        )
      ),
      multiFilter(({ event }) => !excludeTypes.includes(event.type)),
      toEntityModels$()
    );
  }

  static getScheduleSummaryEventablesWithFallback$(
    range: ITimePeriod,
    practice: WithRef<IPractice>,
    practitioners: WithRef<IStaffer>[],
    allStaffSchedules: ICalendarEventSchedulesMap,
    excludeTypes: EventType[] = [],
    unit: CalendarUnit = CalendarUnit.Day,
    view: CalendarView = CalendarView.Timeline
  ): Observable<IScheduleSummaryEventable[]> {
    if (practice.enabledForSchedulingAggregateTrial) {
      const chunkedRange = overlappingChunkRange(range, 1, 'day');
      const practitionerGroups = chunk(practitioners, 10);

      const aggregateData$ = safeCombineLatest(
        practitionerGroups.flatMap((practitionerGroup) =>
          chunkedRange.flatMap((day) =>
            query$(
              Practice.scheduleSummaryCol({
                ref: practice.ref,
              }),
              where('day', '==', toISODate(day.from)),
              where('practice', '==', practice.ref),
              where(
                'staffer',
                'in',
                practitionerGroup.map((staffer) => staffer.ref)
              )
            )
          )
        )
      ).pipe(
        reduce2DArray(),
        concatMap((summaries) =>
          EventableQueries.buildAllEvents(
            summaries,
            allStaffSchedules,
            practice,
            practitioners,
            chunkedRange,
            excludeTypes
          )
        ),
        catchError((error) => {
          // eslint-disable-next-line no-console
          console.error(`Error in aggregateData$: ${error}`);
          return of([]);
        })
      );

      return aggregateData$;
    }

    return from(
      OrganisationCache.brands.getDoc(Practice.brandDoc(practice))
    ).pipe(
      switchMap((brand) =>
        EventableQueries.getEventablesLegacy$(
          brand,
          practice,
          view,
          range,
          unit,
          practice.settings.timezone,
          practitioners,
          excludeTypes
        )
      ),
      catchError((error) => {
        // eslint-disable-next-line no-console
        console.error(`Error in legacyData$: ${error}`);
        return of([]);
      })
    ) as Observable<IScheduleSummaryEventable[]>;
  }

  static async buildAllEvents(
    summaries: WithRef<IScheduleSummary>[],
    allStaffSchedules: ICalendarEventSchedulesMap,
    practice: WithRef<IPractice>,
    practitioners: WithRef<IStaffer>[],
    chunkedRange: ITimePeriod[],
    excludeTypes: EventType[]
  ): Promise<IScheduleSummaryEventable[]> {
    const eventsFromSummaries = await asyncForEach(
      summaries,
      async (summary) => [
        ...summary.events,
        ...buildForecastedEvents(
          summary,
          practice.settings.timezone,
          getCalendarEventSchedules(allStaffSchedules, summary)
        ),
        ...(await buildGapEvents(summary, practice, practitioners)),
      ]
    );

    const forecastedEventsWithoutSummaries = buildFallbackForcastedEvents(
      summaries,
      allStaffSchedules,
      practice,
      practitioners,
      chunkedRange
    );

    const allEvents = [
      ...eventsFromSummaries.flat(),
      ...forecastedEventsWithoutSummaries,
    ];

    return uniqWith(
      allEvents
        .filter(({ event }) => event && !excludeTypes.includes(event.type))
        .map((event) =>
          toEntityModel<IScheduleSummaryEventable | IScheduleSummaryEvent>(
            event
          )
        ),
      (eventA, eventB) => isSameIdentifier(eventA, eventB)
    );
  }
}

export async function buildGapEvents(
  summary: IScheduleSummary,
  practice: WithRef<IPractice>,
  practitioners?: WithRef<IStaffer>[],
  onlyFutureGaps = true
): Promise<IScheduleSummaryEventable[]> {
  const staffer = practitioners?.length
    ? practitioners.find((practitioner) =>
        isSameRef(practitioner, summary.staffer)
      )
    : await OrganisationCache.staff.get.getDoc(summary.staffer);

  if (!staffer) {
    return [];
  }

  const gaps = summary.gaps.map((gap) => buildGapEvent(gap, practice, staffer));
  const timezone = practice.settings.timezone;
  return onlyFutureGaps
    ? gaps.filter((gap) => Gap.isInFuture(gap.event, timezone))
    : gaps;
}

export function buildGapEvent(
  gap: ITimestampRange,
  practice: WithRef<IPractice>,
  staffer: WithRef<IStaffer>
): IScheduleSummaryEventable {
  const event = Event.init({
    ...gap,
    practice,
    type: EventType.Gap,
    participantRefs: [staffer.ref],
    participants: [stafferToParticipant(staffer)],
  });

  const end = toMomentTz(gap.to, practice.settings.timezone);
  const start = toMomentTz(gap.from, practice.settings.timezone);
  const gapDuration = duration(end.diff(start));

  return {
    uid: uuid(),
    event,
    isBlocking: false,
    metadata: {
      label: durationToHumanisedTime(gapDuration),
      tags: [],
      pinnedNotes: [],
      candidates: [],
    },
  };
}

export function buildForecastedEvents(
  summary: IScheduleSummary,
  timezone: Timezone,
  rosterSchedules?: WithRef<ICalendarEventSchedule>[]
): IScheduleSummaryEventable<IAppointment | ICalendarEvent>[] {
  if (!rosterSchedules) {
    return [];
  }
  const blockingEvents = summary.events
    .filter(
      (event) =>
        event.event &&
        event.isBlocking &&
        event.event.type !== EventType.RosteredOn
    )
    .map((event) => toTimePeriod(event.event.from, event.event.to));

  return buildEvents(
    rosterSchedules,
    summary.day,
    timezone,
    summary.practice,
    blockingEvents
  );
}

export function buildFallbackForcastedEvents(
  summaries: WithRef<IScheduleSummary>[],
  allStaffSchedules: ICalendarEventSchedulesMap,
  practice: WithRef<IPractice>,
  practitioners: WithRef<IStaffer>[],
  range: ITimePeriod[]
): IScheduleSummaryEventable<IAppointment | ICalendarEvent>[] {
  const practitionerWithDayGroup = practitioners.flatMap((practitioner) =>
    range.map((day) => ({
      day: toISODate(day.from),
      practitioner,
    }))
  );

  const daysNotLoadedForPractitioners = practitionerWithDayGroup.filter(
    ({ day, practitioner }) => {
      return !summaries.some(
        (summary) =>
          summary.day === day && isSameRef(summary.staffer, practitioner)
      );
    }
  );

  const practitionersGroupedByDay = groupBy(
    daysNotLoadedForPractitioners,
    'practitioner.ref.path'
  );

  return Object.values(practitionersGroupedByDay).flatMap((group) => {
    const timezone = practice.settings.timezone;
    const stafferRef = group[0].practitioner.ref;
    const staffer = practitioners.find((practitioner) =>
      isSameRef(practitioner, stafferRef)
    );
    if (!staffer) {
      return [];
    }
    const rosterSchedules = allStaffSchedules[stafferRef.id] ?? [];
    return group.flatMap(({ day }) =>
      buildEvents(rosterSchedules, day, timezone, practice.ref)
    );
  });
}

function buildEvents(
  rosterSchedules: WithRef<ICalendarEventSchedule>[],
  day: ISODateType,
  timezone: Timezone,
  practiceRef: DocumentReference<IPractice>,
  blockingEvents: ITimePeriod[] = []
): IScheduleSummaryEventable<IAppointment | ICalendarEvent>[] {
  return rosterSchedules
    .filter(
      (rosterSchedule) =>
        rosterSchedule.item.event.type !== EventType.RosteredOn &&
        isSameRef(rosterSchedule.item.event.practice, practiceRef)
    )
    .flatMap((rosterSchedule) => {
      const range = {
        from: toMomentTz(day, timezone).startOf('day'),
        to: toMomentTz(day, timezone).endOf('day'),
      };

      const events = CalendarEventSchedule.buildEventsWithTimezone(
        rosterSchedule,
        range,
        blockingEvents,
        timezone
      );

      return events.map((event) => ({
        uid: uuid(),
        event: event.event,
        isBlocking: event.isBlocking,
        metadata: {
          label: getSchemaText(event.title),
          tags: event.eventTags,
          pinnedNotes: [getSchemaText(event.notes)],
          scheduleRef: event.scheduleRef,
        },
      }));
    });
}

function getCalendarEventSchedules(
  allStaffSchedules: ICalendarEventSchedulesMap,
  summary: IScheduleSummary
): WithRef<ICalendarEventSchedule>[] {
  const calendarEventSchedules = allStaffSchedules[summary.staffer.id];
  return calendarEventSchedules ?? [];
}

function legacyScheduleSummaryEventables$(
  displayRange: ITimePeriod,
  blockingSchedules: WithRef<ICalendarEventSchedule>[],
  appointments: WithRef<IEventable<IAppointment>>[],
  events: WithRef<IEventable>[],
  staff: WithRef<IStaffer>[]
): Observable<IScheduleSummaryEventable<IAppointment | ICalendarEvent>[]> {
  const eventables = [...appointments, ...events];

  const breaks$ = from(
    Staffer.buildEventsFromForecast(staff, blockingSchedules, displayRange, [
      ...appointments,
      ...events,
    ])
  ).pipe(
    multiMap((eventable) => {
      const label = getSchemaText(eventable.title);
      const pinnedNotes = [getSchemaText(eventable.notes)];

      return {
        uid: uuid(),
        event: eventable.event,
        isBlocking: eventable.isBlocking,
        metadata: {
          label,
          tags: eventable.eventTags,
          pinnedNotes,
          scheduleRef: eventable.scheduleRef,
        },
      } as IScheduleSummaryEventable<ICalendarEvent>;
    })
  );

  const summaries = asyncForEach(eventables, (eventable) => {
    const isBlocking = isCalendarEvent(eventable) ? eventable.isBlocking : true;
    return ScheduleSummary.getSummaryFromEventable<
      IAppointment | ICalendarEvent
    >(
      {
        ...eventable,
        event: eventable.event,
        ref: eventable.ref as DocumentReference<IAppointment | ICalendarEvent>,
      },
      isBlocking
    );
  });

  return combineLatest([summaries, breaks$]).pipe(
    reduce2DArray<
      IScheduleSummaryEventable<IAppointment | ICalendarEvent> | undefined
    >(),
    map(compact)
  );
}
